import { useCallback, useMemo, useRef } from "react";
import * as BATTERY_CONFIG from "./BATTERY_CONFIG.js";
import {
  Grid,
  Input,
  Select,
  Switch,
  TextInput,
  useMantineColorScheme,
} from "@mantine/core";
import { IconPercentage, IconCaretDownFilled } from "@tabler/icons-react";
import { getTranslation } from "../../../utils/helpers/index.js";
import { helpAtom } from "../../../utils/atoms/helpAtom.js";
import { useAtom } from "jotai";
import { projectDetailsAtom } from "../../../utils/atoms/projectDetailsAtom.js";
import batteryJson from "../../../components/help/helpCardMenu.json";
import { formatInputValue } from "../../../constants/helper.js";
import { helpContextMenuAtom } from "../../../utils/atoms/helpContextMenuAtom.js";
const BatteryConfigWrapper = ({
  form,
  handleInputChange,
  batteryParameters,
  batteryList,
  handleSelectBatteryChange,
  addAsset,
}) => {
  const [projectDetails] = useAtom(projectDetailsAtom);
  const containerRef = useRef(null);
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  const { colorScheme } = useMantineColorScheme();
  const graySection = useMemo(
    () => ({
      padding: "4px",
    }),
    [colorScheme]
  );

  const handleBlur = (event) => {
    if (!containerRef.current?.contains(event.relatedTarget)) {
      setHelperAtom({
        ...helperAtom,
        isFocused: false,
        helpSection: {
          title: "",
          description: "",
          type: "",
          other: "",
        },
      });
    }
  };

  const handleFocus = (event) => {
    const dataId = event.target.getAttribute("data-id");
    if (dataId) {
      const cardElement = document.querySelector(`[card-id="${dataId}"]`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
        cardElement.style.transition = "background-color 0.5s ease";
        cardElement.style.border = "2px solid #42b4e6";
        setTimeout(() => {
          cardElement.style.border = "";
        }, 2000);
      }
    }
  };

  const getAssetIdValue = (val) => {
    const isValPresent = val === 0 || val ? val : "";
    return isValPresent;
  };

  const [helpMenuData] = useAtom(helpContextMenuAtom);
    
  const getFieldAttributes = (fieldKey) => {
    const field = helpMenuData?.battery?.[fieldKey];
    return {
      title: field?.title || "",
      "data-description": field?.description || "",
      // placeholder: field?.placeholder || "",
    };
  }


  const grayIconStyle = graySection;

  // Responsive column spans: base full width, 2 cols on small, 3 cols on medium, 4 cols on large and above
    // Helper: repeated responsive props for Mantine v6 Grid.Col
    const responsiveCol = { span: 12, sm: 6, md: 4, lg: 4, xl:3 };

  return (
    <div ref={containerRef} onFocus={handleFocus} onBlur={handleBlur}>
      <h5>
        {getTranslation(
          "battery.form.selectIntro",
          "Select a Battery from the dropdown list or customize a new Battery and add it to your user account"
        )}
      </h5>
      <Grid gutter="md" className="mt-2 mb-2" style={{ alignItems: "end" }}>
        <Grid.Col {...responsiveCol}>
          <Input.Wrapper
            label={getTranslation(
              "battery.form.selectBattery",
              "Select Battery"
            )}
            withAsterisk
          >
            <Select
              {...getFieldAttributes("field-1")}
              data-id={"field-1"}
              rightSection={<IconCaretDownFilled size={18} />}
              rightSectionProps={{ style: { pointerEvents: "none" } }}
              data={batteryList}
              value={getAssetIdValue(
                batteryParameters?.uiAttributes?.batteryParameters?.assetId
              )}
              onChange={(value) => {
                const selectedValue = batteryList.find(
                  (option) => option.value === value
                );
                handleSelectBatteryChange(value);
                handleInputChange("uiAttributes", "assetId", value);
                handleInputChange(
                  "uiAttributes",
                  "selectBattery",
                  selectedValue.label
                );
              }}
              withinPortal
              searchable
            />
          </Input.Wrapper>
        </Grid.Col>
        {/* <Grid.Col {...responsiveCol}>
          {batteryParameters?.uiAttributes?.batteryParameters?.assetId ===
            0 && (
              <se-button aria-label="battery-add-custom" onClick={addAsset}>
                {getTranslation("battery.form.addCustom", "Add Custom")}
              </se-button>
            )}
        </Grid.Col> */}
      </Grid>
      <h5> {BATTERY_CONFIG.CONSTANTS.BATTERY_CONFIGURATION}</h5>
      {/* Combined 9 input fields into a single Grid so large screens can flow 4 / 4 / 1 */}
      <Grid gutter="md" className="mt-2 mb-2">
        {/* 1 */}
        <Grid.Col {...responsiveCol}>
          <TextInput
            {...getFieldAttributes("field-2")}
            data-id={"field-2"}
            label={getTranslation("battery.form.ratedCapacity", "Rated/ Nominal Capacity")}
            placeholder={getTranslation(
              "battery.form.ratedCapacity.placeholder",
              "Enter Rated/ Nominal Capacity"
            )}
            required
            value={
              batteryParameters?.componentAttributes?.batteryParameters
                ?.CapaMax || ""
            }
            {...form.getInputProps(
              "componentAttributes.batteryParameters.CapaMax"
            )}
            onChange={(e) =>
              handleInputChange(
                "componentAttributes",
                "CapaMax",
                e.target.value
              )
            }
            rightSection={<div style={{ ...grayIconStyle, marginLeft: "-10px" }}><span size={18}>{"kWh"}</span></div>}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        {/* 2 */}

        {/* 3 */}

        <Grid.Col {...responsiveCol}>
          <TextInput
            required
            label={getTranslation("battery.form.socMin", "Minimum State Of Charge")}
            {...getFieldAttributes("field-3")}
            data-id={"field-3"}
            value={
              batteryParameters?.componentAttributes?.batteryParameters
                ?.SOCMin || ""
            }
            {...form.getInputProps(
              "componentAttributes.batteryParameters.SOCMin"
            )}
            onChange={(e) => {
              const trimmedValue = formatInputValue(e.currentTarget.value);
              handleInputChange("componentAttributes", "SOCMin", trimmedValue);
            }}
            rightSection={<div style={grayIconStyle}><IconPercentage size={18} /></div>}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <TextInput
            required
            label={getTranslation("battery.form.socMax", "Maximum State Of Charge")}
            value={
              batteryParameters?.componentAttributes?.batteryParameters
                ?.SOCMax || ""
            }
            {...getFieldAttributes("field-4")}
            data-id={"field-4"}
            {...form.getInputProps(
              "componentAttributes.batteryParameters.SOCMax"
            )}
            onChange={(e) => {
              const trimmedValue = formatInputValue(e.currentTarget.value);
              handleInputChange("componentAttributes", "SOCMax", trimmedValue);
            }}
            rightSection={<div style={grayIconStyle}><IconPercentage size={18} /></div>}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        {/* 4 */}
        <Grid.Col {...responsiveCol}>
          <TextInput
            label={getTranslation("battery.form.degradation", "Degradation Factor per year")}
            {...getFieldAttributes("field-5")}
            data-id={"field-5"}
            value={
              batteryParameters?.componentAttributes?.batteryParameters
                ?.DegradationFactor || ""
            }
            {...form.getInputProps(
              "componentAttributes.batteryParameters.DegradationFactor"
            )}
            onChange={(e) => {
              const trimmedValue = formatInputValue(e.currentTarget.value);
              handleInputChange(
                "componentAttributes",
                "DegradationFactor",
                trimmedValue
              );
            }}
            rightSection={
              <div
                style={{
                  padding: "4px",
                }}
              >
                <IconPercentage size={18} />
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        {/* 5 */}
        <Grid.Col {...responsiveCol}>
          <TextInput
            {...getFieldAttributes("field-6")}
            data-id={"field-6"}
            label={getTranslation("battery.form.chargeEfficiency", "Charge Efficiency")}
            placeholder={getTranslation("battery.form.value0to100", "Value from 0 to 100")}
            value={
              batteryParameters?.componentAttributes?.batteryParameters
                ?.EfficiencyCharge || ""
            }
            {...form.getInputProps(
              "componentAttributes.batteryParameters.EfficiencyCharge"
            )}
            onChange={(e) => {
              const trimmedValue = formatInputValue(e.currentTarget.value);
              handleInputChange(
                "componentAttributes",
                "EfficiencyCharge",
                trimmedValue
              );
            }}
            required={true}
            rightSection={
              <div
                style={{
                  padding: "4px",
                }}
              >
                {" "}
                <IconPercentage size={18} />
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        {/* 6 */}
        <Grid.Col {...responsiveCol}>
          <TextInput
            {...getFieldAttributes("field-7")}
            data-id={"field-7"}
            placeholder={getTranslation("battery.form.value0to100", "Value from 0 to 100")}
            label={getTranslation("battery.form.dischargeEfficiency", "Discharge Efficiency")}
            value={
              batteryParameters?.componentAttributes?.batteryParameters
                ?.EfficiencyDischarge || ""
            }
            {...form.getInputProps(
              "componentAttributes.batteryParameters.EfficiencyDischarge"
            )}
            onChange={(e) => {
              const trimmedValue = formatInputValue(e.currentTarget.value);
              handleInputChange(
                "componentAttributes",
                "EfficiencyDischarge",
                trimmedValue
              );
            }}
            required={true}
            rightSection={
              <div
                style={{
                  padding: "4px",
                }}
              >
                <IconPercentage size={18} />
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        {/* 7 */}
        <Grid.Col {...responsiveCol}>
          <TextInput
            {...getFieldAttributes("field-8")}
            data-id={"field-8"}
            placeholder={getTranslation(
              "battery.form.lifetimeCycles.placeholder",
              "Number of Lifetime Charging Cycles"
            )}
            label={getTranslation(
              "battery.form.lifetimeCycles",
              "Lifetime Charging and Discharging Cycles"
            )}
            required
            value={
              batteryParameters?.economicAttributes?.batteryParameters
                ?.LifetimeCycle || ""
            }
            {...form.getInputProps(
              "economicAttributes.batteryParameters.LifetimeCycle"
            )}
            onChange={(e) => {
              const trimmedValue = e.currentTarget.value
                .trim()
              handleInputChange(
                "economicAttributes",
                "LifetimeCycle",
                trimmedValue
              );
            }}
            rightSection={
              <div
                style={{
                  padding: "4px",
                }}
              >
                No.
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        {/* 8 */}

        {/* 9 (last) - single column width on large screens (creates 4 / 4 / 1 with empty space) */}
        <Grid.Col {...responsiveCol}>
          <TextInput
            {...getFieldAttributes("field-10")}
            data-id={"field-10"}
            label={getTranslation("battery.form.nominalChargingPower", "Nominal Charging Power")}
            required
            placeholder={getTranslation(
              "battery.form.nominalChargingPower.placeholder",
              "Enter Nominal Charging Power"
            )}
            value={
              batteryParameters?.componentAttributes?.batteryParameters
                ?.MaximumCharge || ""
            }
            {...form.getInputProps(
              "componentAttributes.batteryParameters.MaximumCharge"
            )}
            onChange={(e) =>
              handleInputChange(
                "componentAttributes",
                "MaximumCharge",
                e.target.value
              )
            }
            rightSection={
              <div
                style={{
                  padding: "4px",
                }}
              >
                kW
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <TextInput
          {...getFieldAttributes("field-19")}
            data-id={"field-19"}
            label={getTranslation("battery.form.nominalDischargingPower", "Nominal Discharging Power")}
            required
            value={
              batteryParameters?.componentAttributes?.batteryParameters
                ?.PowElecNominalDischarge || ""
            }
            {...form.getInputProps(
              "componentAttributes.batteryParameters.PowElecNominalDischarge"
            )}
            onChange={(e) =>
              handleInputChange(
                "componentAttributes",
                "PowElecNominalDischarge",
                e.target.value
              )
            }
            rightSection={
              <div
                style={{
                  padding: "4px",
                }}
              >
                kW
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
      </Grid>
    </div>
  );
};

export default BatteryConfigWrapper;
